import Component from '@bythepixel/component-loader';
import {
  DEFAULTS,
  reducedMotionConfig,
  detailedConfig,
  simpleConfig,
  modalConfig,
  thumbnailsConfig,
  regularMotionConfig,
} from '@Front/components/slider/_config';
import { handleComponentError } from '@Core/helpers/functions';
import Swiper from 'swiper';
import 'swiper/css';

export default class Slider extends Component {
  static selector = `.${DEFAULTS.swiper}`;

  constructor(containerEl, options = { ...DEFAULTS }) {
    super(containerEl, options);

    const prefersReducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.reduceMotion = prefersReducedMotionQuery && prefersReducedMotionQuery.matches;

    this.mount();
  }

  mount() {
    const swiper = this.$container;

    switch (true) {
    case swiper.classList.contains(this.$options.swiperInitialized):
    case swiper.classList.contains(this.$options.modalSwiper):
      return;
    case swiper.classList.contains(this.$options.simpleSwiper):
      this.createSwipers(this.$options.simpleSwiper, simpleConfig);
      break;
    case swiper.classList.contains(this.$options.detailedSwiper):
      this.createSwipers(this.$options.detailedSwiper, detailedConfig);
      break;
    case swiper.classList.contains(this.$options.thumbnailsSwiper):
      this.createSwipers(this.$options.thumbnailsSwiper, thumbnailsConfig);
      break;
    default:
      handleComponentError([true, 'No valid slider configuration found.']);
    }
  }

  createSwipers(container, config) {
    const swiperInstances = new Swiper(
      `.${container}`,
      this.configureSwiper(config),
    );

    const swipers = Array.isArray(swiperInstances) ? swiperInstances : [swiperInstances];

    swipers.forEach((swiper) => {
      const linkedSwiperId = swiper.el.getAttribute(this.$options.linkedSwiper);

      if (linkedSwiperId) {
        this.createLinkedSwiper(swiper, linkedSwiperId);
        return;
      }

      this.handleInitialLoad(swiper);
      this.attachLoopedTransitionHandler(swiper);
      this.attachInputHandlers(swiper);
    });
  }

  createLinkedSwiper(controllerSwiper, linkedSwiperId) {
    const linkedSwiper = new Swiper(
      `#${linkedSwiperId}`,
      {
        ...this.configureSwiper(modalConfig),
        thumbs: {
          swiper: controllerSwiper,
        },
        navigation: {
          nextEl: `#${controllerSwiper.el.id} .${this.$options.nextButton}`,
          prevEl: `#${controllerSwiper.el.id} .${this.$options.prevButton}`,
        },
      },
    );

    this.attachFreeTransitionHandler(linkedSwiper);
    this.registerRemoteControllers(linkedSwiper);
  }

  configureSwiper(config) {
    return {
      ...config,
      ...this.handleReducedMotionConfig(),
    };
  }

  handleReducedMotionConfig() {
    return this.reduceMotion ? reducedMotionConfig : regularMotionConfig;
  }

  handleInitialLoad(swiper) {
    this.handleSlideMediaPlayback(swiper.slides[swiper.activeIndex]);
  }

  attachInputHandlers(swiper) {
    swiper.el.addEventListener('click', (e) => {
      if (e.target.hasAttribute(this.$options.playbackButton)) {
        this.handleSlideMediaPlayback(swiper.slides[swiper.activeIndex]);
        return;
      }

      if (e.target.classList.contains(this.$options.scrollbar)) {
        this.handleSlideProgress(swiper, e);
      }
    });
  }

  handleSlideProgress(swiper, e) {
    const scrollbarSize = e.target.offsetWidth;
    const clickPosition = e.clientX - e.target.getBoundingClientRect().left;
    const slideProgress = clickPosition / scrollbarSize;
    const totalSlides = swiper.slides.length;
    const currentSlide = swiper.realIndex;
    const nextSlide = Math.floor(totalSlides * slideProgress);

    if (currentSlide === nextSlide) {
      return;
    }

    swiper.slideToLoop(nextSlide);
    swiper.update();
  }

  attachFreeTransitionHandler(swiper) {
    swiper.on('activeIndexChange', (swiperEvent) => {
      const activeSlide = this.getActiveSlide(swiperEvent);
      const previousSlide = this.getPreviousSlide(swiperEvent);

      this.handleAccessibilityAttributes(activeSlide, true);
      this.handleAccessibilityAttributes(previousSlide, false);

      if (!this.reduceMotion) {
        this.handleSlideMediaPlayback(activeSlide);
        this.handleSlideMediaPlayback(previousSlide);
      }
    });
  }

  attachLoopedTransitionHandler(swiper) {
    // Looped slides don't have reliable indices.
    swiper.on('transitionEnd', (swiperEvent) => {
      swiperEvent.slides.forEach((slide) => {
        this.handleAccessibilityAttributes(slide, this.isActiveSlide(slide));

        if (!this.reduceMotion) {
          this.handleSlideMediaPlayback(slide);
        }
      });
    });
  }

  handleAccessibilityAttributes(slide, show) {
    // We allow this first selector to fail because it's not always present (e.g. thumbnails)
    slide.querySelector(`.${this.$options.slideWrapper}`)?.toggleAttribute('inert', !show);
    slide.setAttribute('aria-hidden', `${!show}`);
  }

  getActiveSlide(swiper) {
    if (swiper.loopedSlides > 0) {
      return swiper.el.querySelector(`.${this.$options.activeSlide}`);
    }

    return swiper.slides[swiper.activeIndex];
  }

  getPreviousSlide(swiper) {
    if (swiper.loopedSlides > 0) {
      return swiper.el.querySelector(`.${this.$options.previousSlide}`);
    }

    return swiper.slides[swiper.previousIndex];
  }

  isActiveSlide(slide) {
    return slide.classList.contains(this.$options.activeSlide);
  }

  registerRemoteControllers(swiper) {
    // Registering them separately allows non-sliders to control sliders, too!
    const remoteControllerEls = document.querySelectorAll(
      `[${this.$options.remoteController}='${swiper.el.id}']`,
    );

    remoteControllerEls.forEach((controllerEl) => {
      const swiperId = controllerEl.getAttribute(this.$options.remoteController);
      const slideId = controllerEl.getAttribute(this.$options.remoteControllerSlide);

      if (!swiperId || !slideId) {
        return;
      }

      controllerEl.addEventListener('click', () => {
        setTimeout(() => this.synchronizeLinkedSwipers(controllerEl, swiperId, slideId), 50);
      });
    });
  }

  synchronizeLinkedSwipers(controllerEl, linkedSwiperId, linkedSlideId) {
    const linkedSwiperElement = document.querySelector(`#${linkedSwiperId}`);
    const controlledSwiper = linkedSwiperElement.swiper;

    if (!controlledSwiper) {
      return;
    }

    controlledSwiper.slideTo(linkedSlideId, 800, false);
    this.synchronizeVideoProgress(controllerEl, controlledSwiper);
  }

  async synchronizeVideoProgress(controllerEl, controlledSwiper) {
    const controllerSwiper = controllerEl.closest(`.${this.$options.swiper}`)?.swiper;

    if (!controllerSwiper) {
      return;
    }

    const controllerSlide = this.getActiveSlide(controllerSwiper);
    const controlledSlide = this.getActiveSlide(controlledSwiper);
    const controllerVideo = controllerSlide?.querySelector('video');
    const controlledVideo = controlledSlide?.querySelector('video');

    if (!controllerVideo) {
      return;
    }

    this.handleSlideMediaPlayback(controllerSlide);

    if (!controlledVideo || controllerVideo.currentTime >= controlledVideo.duration) {
      return;
    }

    await this.handleSlideMediaPlayback(controlledSlide, true).then(() => {
      controlledVideo.currentTime = controllerVideo.currentTime;
    });
  }

  async handleSlideMediaPlayback(slide, forcePlay = false) {
    const slideVideo = slide?.querySelector('video');

    if (!slide || !slideVideo) {
      return;
    }

    const slideIsInert = slide.querySelector(`.${this.$options.slideWrapper}`).inert;
    const slidePlaybackButton = slide.querySelector(`[${this.$options.playbackButton}]`);

    if (slideIsInert || (!slideVideo.paused && forcePlay === false)) {
      this.pauseVideo(slidePlaybackButton, slideVideo);
      return;
    }

    await this.playVideo(slidePlaybackButton, slideVideo);
  }

  pauseVideo(button, video) {
    button?.setAttribute(this.$options.playbackButton, 'paused');
    video.pause();
  }

  async playVideo(button, video) {
    button?.setAttribute(this.$options.playbackButton, 'playing');

    try {
      await video.play();
    } catch (err) {
      this.pauseVideo(button, video);
    }
  }
}
